/**
 * Pure grouping logic for the Timetildeling tab (VIB-1151).
 *
 * Splits a project's budget lines into the buckets the tab renders:
 *   - mainLines: hovedbudsjett (grouped per phase by the caller)
 *   - optionalLines: tilvalg (own rubrikk)
 *   - upsellGroups: ett rubrikk per GODKJENT mersalg (title = description)
 *
 * Lines from non-approved (draft/deleted) upsells are excluded entirely so
 * only approved mersalg surface in Timetildeling. No React/Supabase deps so it
 * is unit-testable in isolation.
 */
import { isSummaryLine, isTaskLine } from "./billing-calculations";
import { PHASE_LABELS, type ProjectPhase } from "./offer-types";

type GroupableLine = {
  is_optional?: boolean | null;
  option_label?: string | null;
  upsell_id?: string | null;
  description?: string | null;
  cost_type?: string | null;
  quantity_hours?: number | null;
};

type PhaseableLine = {
  phase?: string | null;
};

type GroupableUpsell = {
  id: string;
  description: string;
  status: string;
};

export interface UpsellGroup<L> {
  upsellId: string;
  title: string;
  lines: L[];
}

export interface OptionalGroup<L> {
  /** Stabil nøkkel for React (option_label, eller "" for samlet rubrikk). */
  key: string;
  title: string;
  lines: L[];
}

export interface TimetildelingPartition<L> {
  /** Non-summary lines minus non-approved mersalg — basis for totals/costs. */
  regularLines: L[];
  /** Hovedbudsjett: ikke tilvalg, ikke mersalg. */
  mainLines: L[];
  /** Tilvalg-linjer (flat). */
  optionalLines: L[];
  /** VIB-1158: én tilvalg-rubrikk per option_label; linjer uten label samles
   *  i én «Tilvalg»-rubrikk (key ""). Bevarer første-forekomst-rekkefølge. */
  optionalGroups: Array<OptionalGroup<L>>;
  /** Én gruppe per godkjent mersalg som har minst én oppgavelinje. */
  upsellGroups: Array<UpsellGroup<L>>;
}

export function partitionTimetildelingLines<L extends GroupableLine>(
  budgetLines: L[],
  upsells: ReadonlyArray<GroupableUpsell>,
): TimetildelingPartition<L> {
  const approvedUpsellIds = new Set(
    upsells.filter((u) => u.status === "approved").map((u) => u.id),
  );

  // Drop overhead/rabatt summary rows and any line belonging to a
  // non-approved mersalg.
  const regularLines = budgetLines.filter(
    (bl) => !isSummaryLine(bl) && (!bl.upsell_id || approvedUpsellIds.has(bl.upsell_id)),
  );

  const mainLines = regularLines.filter((bl) => !bl.is_optional && !bl.upsell_id);
  const optionalLines = regularLines.filter((bl) => bl.is_optional && !bl.upsell_id);

  // VIB-1158: grupper tilvalg per option_label. Linjer uten label (eldre
  // prosjekter / PDF-import) samles i én «Tilvalg»-rubrikk (key ""). Map
  // bevarer første-forekomst-rekkefølge; den samlede gruppen sorteres sist.
  const optionalByLabel = new Map<string, L[]>();
  for (const bl of optionalLines) {
    const key = bl.option_label ?? "";
    const arr = optionalByLabel.get(key) ?? [];
    arr.push(bl);
    optionalByLabel.set(key, arr);
  }
  const optionalGroups: Array<OptionalGroup<L>> = [...optionalByLabel.entries()]
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : 0))
    .map(([key, lines]) => ({
      key,
      title: key === "" ? "Tilvalg" : `Tilvalg: ${key}`,
      lines,
    }));

  const upsellGroups = upsells
    .filter((u) => u.status === "approved")
    .map((u) => ({
      upsellId: u.id,
      title: `Mersalg: ${u.description}`,
      lines: regularLines.filter((bl) => bl.upsell_id === u.id),
    }))
    // Rene kostnads-mersalg (ingen oppgavelinjer) får ingen rubrikk — deres
    // kostnadslinjer vises i kostnadsseksjonen sammen med hovedbudsjettets.
    .filter((g) => g.lines.some((bl) => isTaskLine(bl)));

  return { regularLines, mainLines, optionalLines, optionalGroups, upsellGroups };
}

export type PhaseGroupKeyLite = ProjectPhase | "annet";

export interface PhaseGroupEntry<L> {
  phase: PhaseGroupKeyLite;
  lines: L[];
}

/**
 * VIB-1230: gjenbrukbar fase-gruppering — nøyaktig samme logikk som
 * `phasesInOrder`-konstruksjonen som tidligere lå inline i
 * BudgetTabContent.tsx for hovedbudsjettets linjer, men generell nok til å
 * kjøres på ETT tilvalgs- eller mersalgs-rubrikk sine linjer også (så
 * Timetildeling kan fase-sortere innen hver rubrikk, ikke bare hovedbudsjettet).
 *
 * Returnerer ALLE kjente faser i PHASE_LABELS-rekkefølge + en avsluttende
 * "annet"-gruppe for linjer med ukjent/tom fase (VIB-723-regelen) —
 * inkludert grupper UTEN linjer. Kallstedet filtrerer selv på
 * `lines.some(isTaskLine)` slik hovedbudsjett-loopen alltid har gjort, så
 * denne funksjonen forblir en ren, ubetinget gruppering (lettere å teste:
 * tom input gir tomme grupper for alle faser, ikke en tom liste).
 */
export function groupLinesByPhase<L extends PhaseableLine>(
  lines: L[],
): PhaseGroupEntry<L>[] {
  const knownPhases = Object.keys(PHASE_LABELS) as ProjectPhase[];
  return [
    ...knownPhases.map((phase) => ({
      phase,
      lines: lines.filter((l) => l.phase === phase),
    })),
    {
      phase: "annet" as const,
      lines: lines.filter(
        (l) => !l.phase || !knownPhases.includes(l.phase as ProjectPhase),
      ),
    },
  ];
}
